import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import SectorCard from '../../components/sector/SectorCard';
import api from '../../utils/api';
import { Sparkle, Compass, Building, Users } from 'lucide-react';
import './Sector.css';

const SectorList = () => {
  const { language } = useLanguage();
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get('/sectors')
      .then((res) => {
        if (!mounted) return;
        const data = Array.isArray(res.data) ? res.data : [];
        const mapped = data.map((s) => ({
          _id: s._id,
          id: s._id,
          title: s.name || s.title || '',
          nepaliTitle: s.name || s.title || '',
          description: s.description || '',
          nepaliDescription: s.description || '',
          slug: s.slug || s.name || s._id,
          icon: s.icon,
          tags: Array.isArray(s.tags) ? s.tags : [],
          subSectors: Array.isArray(s.tags) ? s.tags : [],
          order: typeof s.order === 'number' ? s.order : 999,
        })).sort((a, b) => (a.order || 0) - (b.order || 0));

        setSectors(mapped);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message || 'Failed to load sectors');
        setLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  const content = {
    ne: {
      badge: 'विकास कार्यक्षेत्र',
      title: 'प्राथमिकता क्षेत्रहरू',
      subtitle: 'दिगो, समावेशी र समृद्ध इटहरी निर्माणका लागि पहिचान गरिएका मुख्य विकास क्षेत्रहरू।',
      highlights: [
        { icon: Compass, text: 'दीर्घकालीन भिजन २०५०' },
        { icon: Building, text: 'आधुनिक पूर्वाधार' },
        { icon: Users, text: 'नागरिक सहभागिता' },
      ],
      loading: 'प्राथमिकता क्षेत्रहरू लोड हुँदैछ…',
      empty: 'कुनै प्राथमिकता क्षेत्रहरू उपलब्ध छैन।',
    },
    en: {
      badge: 'Development Scope',
      title: 'Priority Sectors',
      subtitle: 'Key sectors identified for the sustainable, inclusive, and prosperous development of Itahari.',
      highlights: [
        { icon: Compass, text: 'Long Vision 2050' },
        { icon: Building, text: 'Modern Infrastructure' },
        { icon: Users, text: 'Citizen Participation' },
      ],
      loading: 'Loading priority sectors…',
      empty: 'No priority sectors available.',
    },
  }[language] || {
    badge: 'विकास कार्यक्षेत्र',
    title: 'प्राथमिकता क्षेत्रहरू',
    subtitle: 'दिगो, समावेशी र समृद्ध इटहरी निर्माणका लागि पहिचान गरिएका मुख्य विकास क्षेत्रहरू।',
    highlights: [
      { icon: Compass, text: 'दीर्घकालीन भिजन २०५०' },
      { icon: Building, text: 'आधुनिक पूर्वाधार' },
      { icon: Users, text: 'नागरिक सहभागिता' },
    ],
    loading: 'प्राथमिकता क्षेत्रहरू लोड हुँदैछ…',
    empty: 'कुनै प्राथमिकता क्षेत्रहरू उपलब्ध छैन।',
  };

  return (
    <main className={`sector-list page-enter sector-list--${language}`}>
      {/* ════════════════════════════════════════════════
          REDESIGNED HERO SECTION
      ════════════════════════════════════════════════ */}
      <section className="sector-list__hero" aria-labelledby="sector-page-heading">
        <div className="sector-list__hero-bg" aria-hidden="true" />
        <div className="sector-list__hero-orb sector-list__hero-orb--1" aria-hidden="true" />
        <div className="sector-list__hero-orb sector-list__hero-orb--2" aria-hidden="true" />

        <div className="container">
          <div className="sector-list__hero-content">
            {/* Eyebrow Badge */}
            <div className="sector-list__badge">
              <span className="sector-list__badge-dot" aria-hidden="true" />
              <span className="sector-list__badge-text">{content.badge}</span>
            </div>

            {/* Main Title (Kept exact: "प्राथमिकता क्षेत्रहरू") */}
            <h1 className="sector-list__title" id="sector-page-heading">
              {content.title}
            </h1>

            {/* Hero Subtitle */}
            <p className="sector-list__subtitle">
              {content.subtitle}
            </p>

            {/* Subtle Civic Highlights Strip */}
            <div className="sector-list__highlights">
              {content.highlights.map((item, idx) => {
                const ItemIcon = item.icon;
                return (
                  <div key={idx} className="sector-list__highlight-item">
                    <ItemIcon size={14} className="sector-list__highlight-icon" />
                    <span>{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          SECTORS GRID SECTION
      ════════════════════════════════════════════════ */}
      <section className="sector-list__content" aria-label="Priority Sectors List">
        <div className="container">
          {loading ? (
            <div className="sector-list__loading">
              <div className="sector-list__spinner" aria-hidden="true" />
              <p>{content.loading}</p>
            </div>
          ) : error ? (
            <div className="sector-list__error">
              <p>{error}</p>
            </div>
          ) : sectors.length === 0 ? (
            <div className="sector-list__empty">
              <p>{content.empty}</p>
            </div>
          ) : (
            <div className="sectors-grid" role="grid">
              {sectors.map((sector) => (
                <div key={sector.id} role="gridcell" className="sectors-grid__item">
                  <SectorCard sector={sector} language={language} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default SectorList;